import React, { useState } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';

export default function BrokerChangePassword() {
    const { token } = useAuth();
    const { theme } = useTheme();
    const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMsg(null);
        if (!token) { setMsg({ type: 'error', text: 'Please login first.' }); return; }
        if (form.newPassword !== form.confirmPassword) { setMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
        if (form.newPassword.length < 6) { setMsg({ type: 'error', text: 'Password must be at least 6 characters.' }); return; }
        
        setLoading(true);
        try {
            const res = await api.changePassword(token, { oldPassword: form.oldPassword, newPassword: form.newPassword });
            if (res.message === 'Password changed successfully') {
                setMsg({ type: 'success', text: 'Security credentials updated successfully!' });
                setForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                setMsg({ type: 'error', text: res.message || 'Failed to update password.' });
            }
        } catch (error) {
            setMsg({ type: 'error', text: 'An unexpected security error occurred.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <UserDashboardLayout>
            <div className="max-w-4xl mx-auto py-8">
                {/* Header Section */}
                <div className="mb-12 px-4 animate__animated animate__fadeInDown">
                    <h1 className="text-4xl md:text-5xl font-black text-[var(--text-primary)] mb-3 tracking-tighter uppercase italic">Vault Access</h1>
                    <p className="text-[var(--text-secondary)] font-black text-xs uppercase tracking-[0.2em] opacity-70">Re-key your cryptographic security credentials for maximum account hardening.</p>
                </div>

                {msg && (
                    <div className={`mx-4 p-6 rounded-[2rem] mb-10 flex items-center gap-4 border animate__animated animate__shakeX ${
                        msg.type === 'success' 
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                        : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                    }`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${msg.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white shadow-lg'}`}>
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                        </div>
                        <span className="font-black uppercase tracking-widest text-xs italic">{msg.text}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 px-4 mb-20 animate__animated animate__fadeIn">
                    <div className="lg:col-span-2">
                        <div className="glass-card p-8 md:p-12 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--purple)]/5 rounded-bl-full"></div>
                            
                            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] ml-2 opacity-60 italic">Current Security Key</label>
                                    <input 
                                        type="password" 
                                        className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-black text-[var(--text-primary)] shadow-inner tracking-widest" 
                                        name="oldPassword" 
                                        value={form.oldPassword} 
                                        onChange={handleChange} 
                                        placeholder="••••••••••••"
                                        required 
                                    />
                                </div>
                                
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] ml-2 opacity-60 italic">New Cryptographic Code</label>
                                    <input 
                                        type="password" 
                                        className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-black text-[var(--text-primary)] shadow-inner tracking-widest" 
                                        name="newPassword" 
                                        value={form.newPassword} 
                                        onChange={handleChange} 
                                        placeholder="Min 6 characters"
                                        required 
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] ml-2 opacity-60 italic">Confirm New Protocol</label>
                                    <input 
                                        type="password" 
                                        className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-black text-[var(--text-primary)] shadow-inner tracking-widest" 
                                        name="confirmPassword" 
                                        value={form.confirmPassword} 
                                        onChange={handleChange} 
                                        placeholder="Repeat for confirmation"
                                        required 
                                    />
                                </div>
                                
                                <div className="pt-6">
                                    <button 
                                        className="btn-purple w-full py-5 text-[11px] no-underline shadow-2xl relative overflow-hidden group/btn" 
                                        type="submit" 
                                        disabled={loading}
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-4">
                                            {loading ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-key-skeleton group-hover:rotate-12 transition-transform"></i>}
                                            {loading ? 'Encrypting Credentials...' : 'Overwrite Security Matrix'}
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div className="glass-card p-8 border-[var(--purple)]/10 shadow-xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-2 h-full bg-[var(--purple)]/20"></div>
                            <h3 className="text-[10px] font-black text-[var(--text-primary)] uppercase tracking-[0.3em] mb-6 italic opacity-80">Encryption Nodes</h3>
                            <ul className="space-y-5">
                                {[
                                    'Length > 6 Characters',
                                    'Alphanumeric Stream',
                                    'Unique Cipher Pattern',
                                    'Case Sensitivity Active'
                                ].map((req, i) => (
                                    <li key={i} className="flex items-center gap-4 text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-tight italic opacity-60 hover:opacity-100 transition-opacity">
                                        <div className="w-5 h-5 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                            <i className="fa-solid fa-shield-check text-emerald-500 text-[10px]"></i>
                                        </div>
                                        {req}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-[var(--gold)]/10 rounded-[2.5rem] p-8 border border-[var(--gold)]/20 shadow-2xl relative overflow-hidden group">
                            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[var(--gold)]/10 rounded-full blur-3xl"></div>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-[var(--gold)] text-black flex items-center justify-center shadow-lg">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                </div>
                                <h3 className="text-[10px] font-black text-black uppercase tracking-[0.3em] italic">Critical Alert</h3>
                            </div>
                            <p className="text-[10px] font-black text-black/60 uppercase tracking-tight italic leading-loose">
                                Initializing a credential overwrite will automatically terminate all other active network sessions for node security.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </UserDashboardLayout>
    );
}
